<template>
  <section
    class="landing"
    aria-labelledby="landing-title"
    style="background-image: url('/images/back.jpg?v=2')"
  >
    <div class="landing__veil" aria-hidden="true"></div>
    <div class="landing__stage">
    <aside class="landing__rail landing__rail--left" aria-hidden="true">
      <img src="/images/ampl.jpg" alt="">
    </aside>
    <div class="container landing__inner">
      <div class="landing__copy">
        <p class="landing__kicker">TCP / NTCP Calculator</p>
        <h1 id="landing-title" class="landing__title">
          Радиобиологические расчеты
          <span>в области лучевой терапии</span>
        </h1>
        <p class="landing__lead">
          Вероятность установления контроля над ростом опухоли
          (<strong>TCP</strong> — Tumor Control Probability)
          и вероятность развития пострадиационных осложнений
          со стороны здоровых тканей
          (<strong>NTCP</strong> — Normal Tissue Complication Probability)
        </p>
        <router-link
          v-if="userStore.user"
          to="/app/calculate"
          class="landing__cta"
        >
          Начать расчет
          <i class="fas fa-arrow-right" aria-hidden="true"></i>
        </router-link>
      </div>

      <div class="landing__cards">
        <article
          v-for="(card, index) in cards"
          :key="card.title"
          class="landing-card"
          :style="{ '--delay': `${0.18 + index * 0.1}s` }"
        >
          <div class="landing-card__head">
            <img
              class="landing-card__icon"
              :src="card.image"
              alt=""
              aria-hidden="true"
            >
            <h2 class="landing-card__title">{{ card.title }}</h2>
          </div>
          <ul class="landing-card__list">
            <li v-for="item in card.items" :key="item">{{ item }}</li>
          </ul>
        </article>
      </div>
    </div>
    <aside class="landing__rail landing__rail--right" aria-hidden="true">
      <img src="/images/dose-curves.png?v=2" alt="">
    </aside>
    </div>
  </section>
</template>

<script>
import { useUserStore } from '../stores/userStore';

export default {
  name: 'MainPage',
  setup() {
    const userStore = useUserStore();
    const cards = [
      {
        title: 'Инструмент',
        image: '/images/card-tool.png',
        items: [
          'Для медицинских физиков и радиационных онкологов',
          'Для оценки эффективности и безопасности облучения',
          'Для поддержки в принятии клинических решений',
        ],
      },
      {
        title: 'Сравнение стратегий',
        image: '/images/card-compare.png',
        items: [
          'Гистограммы «доза–объем» для опухолей и органов риска',
          'Режимы облучения',
          'Виды радиобиологических моделей',
          'Значения параметров моделей',
          'Терапевтический индекс',
        ],
      },
      {
        title: 'Результаты и отчеты',
        image: '/images/card-results.png',
        items: [
          'Таблицы расчетов',
          'Графики кривых TCP и NTCP',
          'Быстро, удобно и наглядно',
          'Экспорт отчетов и результатов',
        ],
      },
    ];
    return { userStore, cards };
  },
};
</script>

<style scoped>
.landing {
  --landing-cyan: #7ee7ff;
  --landing-cyan-soft: rgba(126, 231, 255, 0.18);
  --landing-ink: #e8f4ff;
  position: relative;
  isolation: isolate;
  min-height: calc(100vh - 80px);
  display: flex;
  align-items: stretch;
  overflow: hidden;
  color: var(--landing-ink);
  background-color: #020617;
  background-repeat: no-repeat;
  background-size: cover;
  background-position: 78% 36%;
}

.landing__veil {
  position: absolute;
  inset: 0;
  z-index: 0;
  pointer-events: none;
  background:
    linear-gradient(
      105deg,
      rgba(1, 6, 16, 0.78) 0%,
      rgba(1, 8, 20, 0.52) 34%,
      rgba(2, 10, 24, 0.12) 58%,
      rgba(2, 10, 24, 0.28) 100%
    ),
    linear-gradient(
      180deg,
      rgba(2, 8, 20, 0.18) 0%,
      rgba(2, 8, 20, 0.08) 42%,
      rgba(1, 6, 16, 0.55) 100%
    );
}

.landing__stage {
  position: relative;
  z-index: 1;
  display: flex;
  align-items: stretch;
  justify-content: center;
  flex: 1 1 auto;
  width: 100%;
  min-width: 0;
}

.landing__rail {
  display: none;
  pointer-events: none;
  user-select: none;
}

.landing__rail img {
  display: block;
  width: 100%;
  height: auto;
  object-fit: contain;
  opacity: 0.52;
  mix-blend-mode: screen;
}

.landing__rail--left {
  -webkit-mask-image: linear-gradient(90deg, #000 62%, transparent 100%);
  mask-image: linear-gradient(90deg, #000 62%, transparent 100%);
}

.landing__rail--left img {
  object-position: left top;
  width: 100%;
  max-height: min(98vh, 1176px);
  object-fit: contain;
  opacity: 0.7;
  transform: translateX(-1.5rem);
}

.landing__rail--right {
  -webkit-mask-image: linear-gradient(270deg, #000 78%, transparent 100%);
  mask-image: linear-gradient(270deg, #000 78%, transparent 100%);
}

.landing__rail--right img {
  object-position: right top;
  -webkit-mask-image: linear-gradient(to bottom, #000 91%, transparent 100%);
  mask-image: linear-gradient(to bottom, #000 91%, transparent 100%);
}

.landing__inner {
  position: relative;
  z-index: 2;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  gap: 2.75rem;
  width: 100%;
  min-width: 0;
  padding-top: 3.25rem;
  padding-bottom: 2.75rem;
}

@media (min-width: 1400px) {
  .landing__rail {
    display: block;
    position: absolute;
    top: 0;
    z-index: 0;
    width: clamp(240px, 21vw, 420px);
    padding-top: 0;
  }

  .landing__rail--left {
    left: 0;
    width: clamp(264px, 24vw, 456px);
    padding-top: 1.25rem;
  }

  .landing__rail--right {
    right: 0;
    width: clamp(312px, 27.3vw, 546px);
    padding-top: 1.75rem;
  }

  .landing__inner {
    flex: 1 1 auto;
    max-width: 1140px;
  }
}

@media (min-width: 1680px) {
  .landing__rail--right {
    width: clamp(367px, 21.84vw, 437px);
    padding-top: 2.75rem;
  }
}

@media (min-width: 1800px) {
  .landing__inner {
    max-width: 1320px;
  }
}

@media (min-width: 992px) and (min-height: 1000px) {
  .landing__inner {
    justify-content: center;
    gap: clamp(2.25rem, 3.2vh, 3rem);
  }
}

.landing__copy {
  max-width: 38rem;
  animation: landing-rise 0.85s cubic-bezier(0.22, 1, 0.36, 1) both;
}

.landing__kicker {
  margin: 0 0 0.85rem;
  font-size: 0.78rem;
  font-weight: 600;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: var(--landing-cyan);
}

.landing__title {
  margin: 0 0 1.35rem;
  font-size: clamp(2.05rem, 4.2vw, 3.35rem);
  font-weight: 700;
  line-height: 1.12;
  letter-spacing: -0.02em;
  color: #f7fbff;
}

.landing__title span {
  display: block;
  font-weight: 500;
  color: rgba(236, 247, 255, 0.88);
}

.landing__lead {
  margin: 0;
  max-width: 34rem;
  font-size: 1.05rem;
  line-height: 1.7;
  color: rgba(214, 232, 248, 0.82);
}

.landing__lead strong {
  color: #f4fbff;
  font-weight: 650;
}

.landing__cta {
  display: inline-flex;
  align-items: center;
  gap: 0.7rem;
  margin-top: 1.75rem;
  padding: 0.85rem 1.45rem;
  border-radius: 999px;
  border: 1px solid rgba(126, 231, 255, 0.45);
  background: linear-gradient(180deg, rgba(126, 231, 255, 0.22), rgba(126, 231, 255, 0.08));
  color: #f7fbff;
  font-weight: 600;
  text-decoration: none;
  box-shadow: 0 10px 28px rgba(0, 0, 0, 0.28), inset 0 1px 0 rgba(255, 255, 255, 0.18);
  transition: transform 0.25s ease, box-shadow 0.25s ease, background 0.25s ease;
}

.landing__cta:hover {
  color: #fff;
  transform: translateY(-2px);
  background: linear-gradient(180deg, rgba(126, 231, 255, 0.34), rgba(126, 231, 255, 0.12));
  box-shadow: 0 14px 32px rgba(0, 0, 0, 0.35), inset 0 1px 0 rgba(255, 255, 255, 0.22);
}

.landing__cards {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 1.15rem;
}

.landing-card {
  min-height: 100%;
  padding: 1.35rem 1.3rem 1.2rem;
  border-radius: 18px;
  background: linear-gradient(180deg, rgba(10, 22, 42, 0.62) 0%, rgba(5, 12, 26, 0.72) 100%);
  border: 1px solid rgba(146, 210, 255, 0.18);
  box-shadow:
    0 18px 40px rgba(0, 0, 0, 0.28),
    inset 0 1px 0 rgba(255, 255, 255, 0.08);
  backdrop-filter: blur(16px) saturate(140%);
  -webkit-backdrop-filter: blur(16px) saturate(140%);
  animation: landing-rise 0.9s cubic-bezier(0.22, 1, 0.36, 1) both;
  animation-delay: var(--delay);
  transition: transform 0.28s ease, border-color 0.28s ease, box-shadow 0.28s ease;
}

.landing-card:hover {
  transform: translateY(-4px);
  border-color: rgba(126, 231, 255, 0.42);
  box-shadow:
    0 22px 46px rgba(0, 0, 0, 0.34),
    0 0 0 1px rgba(126, 231, 255, 0.08),
    inset 0 1px 0 rgba(255, 255, 255, 0.12);
}

.landing-card__head {
  display: flex;
  align-items: center;
  gap: 0.85rem;
  margin-bottom: 1rem;
}

.landing-card__icon {
  width: 2.4rem;
  height: 2.4rem;
  flex-shrink: 0;
  object-fit: contain;
  pointer-events: none;
  user-select: none;
  filter:
    drop-shadow(0 0 3px rgba(126, 231, 255, 0.95))
    drop-shadow(0 0 10px rgba(126, 231, 255, 0.55))
    drop-shadow(0 0 18px rgba(102, 160, 255, 0.35));
}

.landing-card__title {
  margin: 0;
  font-size: 1.12rem;
  font-weight: 700;
  line-height: 1.25;
  color: #f7fbff;
}

.landing-card__list {
  margin: 0;
  padding: 0;
  list-style: none;
  display: grid;
  gap: 0.55rem;
}

.landing-card__list li {
  position: relative;
  padding-left: 1rem;
  font-size: 0.92rem;
  line-height: 1.45;
  color: rgba(214, 232, 248, 0.86);
}

.landing-card__list li::before {
  content: '';
  position: absolute;
  left: 0;
  top: 0.55em;
  width: 0.38rem;
  height: 0.38rem;
  border-radius: 50%;
  background: var(--landing-cyan);
  box-shadow: 0 0 8px rgba(126, 231, 255, 0.7);
}

@keyframes landing-rise {
  from {
    opacity: 0;
    transform: translateY(18px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@media (max-width: 991.98px) {
  .landing {
    background-position: 86% 32%;
    min-height: calc(100vh - 80px);
  }

  .landing__veil {
    background:
      linear-gradient(
        180deg,
        rgba(1, 6, 16, 0.72) 0%,
        rgba(1, 8, 20, 0.42) 38%,
        rgba(1, 6, 16, 0.78) 100%
      );
  }

  .landing__cards {
    grid-template-columns: 1fr;
  }

  .landing__copy {
    max-width: none;
  }
}

@media (max-width: 575.98px) {
  .landing__inner {
    padding-top: 2.25rem;
    padding-bottom: 1.75rem;
    gap: 1.75rem;
  }

  .landing__title {
    font-size: 1.85rem;
  }

  .landing__lead {
    font-size: 0.98rem;
  }
}

@media (prefers-reduced-motion: reduce) {
  .landing__copy,
  .landing-card,
  .landing__cta {
    animation: none;
    transition: none;
  }
}
</style>
